import { useState, useEffect, useMemo } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAPICall } from '../../../../../hooks';
import type { AttachmentImageBA } from '../../../../../sdk/type';
import { getRoundedNumber, getRoundedNumber2 } from '../../../../../ui/utils/common';
import {
  LoggerService,
  convertGMT8date,
  dateTimeToCustomFormat,
  formatDateDMYYYY,
  resolveProfileImageSectionShown,
  resolveSubKpiCode,
  resolveCheckInDays,
  resolveFileUploader,
  resolveTestDeviceName,
  filterSubKpiByDevice,
  resolveGenderAgeThreshold,
  resolveSubChallenge,
} from '../../../../../ui/utils';
import ParticipantService from '../../../../../sdk/services/participantService';
import PinService from '../../../../../sdk/services/pinService';
import RewardService from '../../../../../sdk/services/rewardService';
import PhotoUnavailable from '../../../../../assets/image/sample/photoUnavailable.png';
import Personalize from '../../../../../assets/image/personalize.png';
import {
  AdminPageHeader,
  ButtonType,
  Card,
  FAIcon,
  HeaderStyle,
  IconButtonProps,
  IconStyle,
  ImageSlider,
  Theme,
  ProfileImage,
  TableContainer,
  Tag,
  TagStyle,
  RadioButton,
  Tooltip,
  TooltipPreference,
  // Button,
  // ButtonTypes,
  // ButtonSize,
} from '../../../../../ui/components';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import { TableWithAction} from '../../../../../ui/components/TableWithAction/TableWithAction';
import { ActionType } from '../../../../../ui/components/types/enums';
import { decryptString, encryptString } from '../../../../../ui/utils/cryptoUtils';
//import { HeaderFlex } from '../../../../../ui/components/Form/Card/types/enums';
import { DynamicObjectInterface } from '../../../../../sdk/type';
import AttachmentService from '../../../../../sdk/services/attachmentService';
import healthGoalService from '../../../../../sdk/services/healthGoalService';
import intermediateMeasurementService from '../../../../../sdk/services/intermediateMeasurementService';
import AuditLogService from '../../../../../sdk/services/auditLogService';
import AuditHistoryAccordion from '../../../audit-log/auditLogAccordion';
import { DetailsModal, HealthGoalModal, PointAllocationModal, ResultModal, RewardGiftModal } from './editModal';
import getRewardListConfig from './participantDetailsConfig';
import getCheckInColumns from './checkInDetailsConfig';
import styles from './index.module.scss';

export interface ParticipantData {
  recId?: string;
  campaign: {
    campaignName: string;
    campaignStart: string;
    campaignEnd: string;
    campaignId: string;
    configJson: DynamicObjectInterface;
  };
  userId: string;
  coachID: string;
  coachName: string;
  enrollmentStatus?: string;
  email: string;
  isNewAbo: number;
  loginId: string;
  partyId: string;
  name: string;
  phone: string;
  pinLevel: string;
  memberType: string;
  // Used for gender/age-threshold assessment indicators (e.g. Grip Strength).
  gender?: string;
  ageGroup?: string;
  point: PointData;
  purchase: {
    recId?: number;
    orderPoint?: number;
    orderNumber?: string;
    orderName?: string;
    orderStatus?: number;
  }[];
  remark: string;
  rewards: {
    rewardRemarks?: string;
    rewardId?: number;
    rewardCode?: string;
    rewardName?: string;
    isNew?: boolean;
    rewardRecId: string;
    rewardStatus?: string;
    modifiedDate?: string;
    rewardType?: string;
  }[];
  status: string;
  subscribe: {
    recId?: number;
    orderPoint?: number;
    orderNumber?: string;
    orderStatus?: number;
  }[];
  team: {
    teamId: string;
    teamName: string;
    teamStartDate: string;
    teamEndDate: string;
    teamLeaderLoginId: string;
    teamLeaderName: string;
    participationType: string;
  };
  weightIn: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    machine: string;
    otherIndicators: DynamicObjectInterface;
    weightedDate: string;
    remarks: string;
    file?: { [fieldCode: string]: { file?: File; url: string; type?: string }[] };
  };
  weightOut: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    otherIndicators: DynamicObjectInterface;
    machine: string;
    weightedDate: string;
    remarks: string;
    file?: { [fieldCode: string]: { file?: File; url: string; type?: string }[] };
  };
  weightOutDiffValues: {
    result?: boolean;
  };
  isIndividualAchieved?: number;
  isIndividualCompleted?: number;
  isGroupAchieved?: number;
  // Participant's assigned sub-challenge (MULTI campaigns). Used to resolve the
  // per-sub-challenge Front/Side Profile Image Section flag.
  subChallengeId?: string;
  // Check-in submissions (from the BE getParticipantDetailData mapping).
  checkIns?: { recId?: number; day: number | null; checkInDate?: string | null; remarks?: string | null }[];
}

export interface PointData {
  purchase: string;
  purchaseAdj: string;
  subscribe: string;
  subscribeAdj: string;
  winning: string;
  winningAdj: string;
  remarks: string;
}

export interface MasterData {
  pin: {
    pinId: number;
    pinName: string;
  }[];
  reward: {
    recId: number;
    rewardCode: string;
    rewardName: string;
  }[];
}

export interface SearchParams {
  campaignId: string;
  teamId: string;
  userId: string;
}

export interface UserAnswerDataType {
  answerId: number;
  questionType: string;
  answerText: string;
  options: DynamicObjectInterface[] | null;
}

export interface CampaignQuestionType {
  questionRecId: number;
  questionText: string;
  questionType: string;
  options: null;
}

export interface ProgressType {
  weighingDate: Date;
  checked: boolean;
  fileAvailable?: boolean;
}

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  ParticipantDetails: 'participantDetails',
  HealthGoal: 'healthGoal',
  PointAllocation: 'pointAllocation',
  RewardGift: 'rewardGift',
  Result: 'weightDetails',
} as const;

export const AdminParticipantDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  const [data, setData] = useState<ParticipantData | null>(null);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [isLoading, setIsLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');
  const [userAnswerData, setUserAnswerData] = useState<UserAnswerDataType>();
  //const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [healthQuestionData, setHealthQuestionData] = useState<CampaignQuestionType>();
  const [progressData, setProgressData] = useState<ProgressType[]>([]);
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let keys: SearchParams = {
      campaignId: params.get('campaignId') ?? '',
      teamId: params.get('teamId') ?? '',
      userId: params.get('userId') ?? '',
    };
    setSearchParams(keys);
  }, [location]);

  useEffect(() => {
    if (searchParams) {
      const fetchData = async () => {
        await fetchParticipantDetails();
        fetchMasterData();
        fetchProfileImage();
        fetchMeasurementImage();
        fetchHealthGoalAnswer();
        fetchHealthGoalQuestion();
        fetchAuditLogs();
      };

      fetchData();
    }
  }, [searchParams]);


  useEffect(() => {
    if (!data) return;
    fetchProgressMonitoringList();
  }, [data]);


  const fetchAuditLogs = async () => {
    try {
      if (searchParams?.campaignId && searchParams?.teamId && searchParams?.userId) {
        const payload = {
          campaignId: searchParams?.campaignId,
          teamId: searchParams?.teamId,
          userId: searchParams?.userId,
        };

        const historyLogResponse = await apiCall(AuditLogService.fetchLog, payload);
        const historyLogResult = historyLogResponse.data;

        setAuditHistoryLog(historyLogResult.data || []);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve audit history log', error);
    }
  };

  const fetchProgressMonitoringList = async () => {
    const info = {
      loginId: data?.loginId,
      userId: searchParams?.userId,
      campaignId: searchParams?.campaignId,
    };

    try {
      const result = await apiCall(intermediateMeasurementService.getIntermediateMeasurementList, info);
      setProgressData(result?.data?.data);
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(searchParams?.userId ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  // Assessment indicators resolved from the participant's sub-challenge (MULTI) or
  // the legacy campaign-level list (SINGLE/legacy).
  const resolvedSubKpiCode = resolveSubKpiCode(data?.campaign?.configJson, data?.subChallengeId);
  // When indicators are tagged to test devices, show only the ones for the device
  // the participant used (pre uses weightIn.machine, post uses weightOut.machine),
  // mirroring the participant My Progress display.
  const preSubKpiCode = filterSubKpiByDevice(resolvedSubKpiCode, data?.weightIn?.machine);
  const postSubKpiCode = filterSubKpiByDevice(resolvedSubKpiCode, data?.weightOut?.machine);

  // Check-in submissions (read-only): configured days for the participant's
  // sub-challenge, joined with the participant's submissions + uploaded proof files.
  const checkInDays = resolveCheckInDays(data?.campaign?.configJson, data?.subChallengeId);
  const submittedCheckInByDay = new Map<number, DynamicObjectInterface>(
    (data?.checkIns ?? [])
      .filter(c => c?.day != null && !isNaN(Number(c.day)))
      .map(c => [Number(c.day), c] as [number, DynamicObjectInterface]),
  );

  const subKpiListDisplayInProgressMonitoring =
      resolvedSubKpiCode?.filter(
        (i: DynamicObjectInterface) => i.displayInProgressMonitoring === 'on',
      ) || [];
  
  const progressMonitorConfig = [
    {
      key: 'weighingDate',
      text: t('dashboard.my-progress.input.date'),
      sortable: true,
      render: (object: DynamicObjectInterface) => (
        <span>{dateTimeToCustomFormat(object.weighingDate, 'DD/MM/YYYY')}</span>
      ),
    },
    ...(subKpiListDisplayInProgressMonitoring || []).map((kpi: DynamicObjectInterface) => ({
      key: kpi.subKpiCode,
      text: `${kpi.indicatorName} (${kpi.indicatorMeasurementUnit})`,
      sortable: false,
      render: (object: DynamicObjectInterface) => {
        const otherIndicators = JSON.parse(object.otherIndicators);
        return (
          <span>
            {otherIndicators[kpi.subKpiCode]
              ? getRoundedNumber(parseFloat(decryptString(otherIndicators[kpi.subKpiCode])))
              : ''}
          </span>
        );
      },
    })),
  ];

  const downloadIntermediateResult = async (row :any) => {

    try {
      const payload = {
        loginId: searchParams?.userId,
        campaignId: searchParams?.campaignId,
        teamId: searchParams?.teamId,
        assessmentType: 'intermediate',
        refId: row.recId,
      };

      const recordDate = row.weighingDate.split(' ')[0].replaceAll('-', '');

      const response = await apiCall(AttachmentService.downloadOther, payload);

      //console.log('response', response);
      if (response.status === 200 && !response.data?.data?.message) {
        const data = response.data;
        const blob = new Blob([data], { type: 'application/zip' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', recordDate + '_'  + searchParams?.userId +  '_intermediate_result.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({
          message: response?.data?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error downloading attachment', error);
    }
  };

  const [images, setImages] = useState<Record<string, AttachmentImageBA>>({});
  // Check-in proof files keyed by campaign day, for the read-only check-in table.
  const [checkInFiles, setCheckInFiles] = useState<{
    [day: number]: { photoId: number; fileName: string; type?: string }[];
  }>({});

  // Read-only check-in table rows: configured day + submission + uploaded files.
  const checkInTableData = checkInDays.map(day => {
    const submitted = submittedCheckInByDay.get(day);
    return {
      key: `checkin-${day}`,
      day,
      submitted: !!submitted,
      checkInDate: submitted?.checkInDate ?? null,
      files: checkInFiles[day] ?? [],
    };
  });

  const handleDownloadCheckInFile = async (photoId: number, fileName: string) => {
    try {
      const response = await apiCall(AttachmentService.downloadSelected, { recId: photoId });
      if (response.status === 200 && !response.data?.data?.message) {
        const blob = response.data as Blob;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', fileName || 'attachment');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({ message: response?.data?.data?.message, theme: Theme.ERROR });
      }
    } catch (error) {
      LoggerService.log('Error downloading check-in attachment', error);
    }
  };

  const fetchMeasurementImage = async () => {
    if (searchParams) {
      setIsLoading(true);
      try {
        const payload = {
          campaignId: searchParams?.campaignId,
          teamId: searchParams?.teamId,
          loginId: searchParams?.userId,
        };
        const response = await AttachmentService.downloadBeforeAfter(payload);

        const imageData = response?.data?.data?.image || [];

        const updatedImages = { ...images };
        const checkInFileMap: { [day: number]: { photoId: number; fileName: string; type?: string }[] } = {};
        // Readable file name from the (URL-encoded) S3 key.
        const fileNameFromUrl = (u: string): string => {
          try {
            const path = decodeURIComponent(u.split('?')[0]);
            return path.substring(path.lastIndexOf('/') + 1) || 'attachment';
          } catch {
            return 'attachment';
          }
        };

        imageData.forEach((image: AttachmentImageBA) => {
          const { photoId, photoType, url } = image;
          if (photoType) {
            updatedImages[photoType] = { photoId, url };
          }
          // photoType format: checkin_day{N}_{fieldCode}_{idx}
          if (photoType?.startsWith('checkin_day')) {
            const day = Number(photoType.replace('checkin_day', '').split('_')[0]);
            if (!isNaN(day)) {
              if (!checkInFileMap[day]) checkInFileMap[day] = [];
              checkInFileMap[day].push({ photoId, fileName: fileNameFromUrl(url ?? ''), type: image?.type });
            }
          }
        });

        setImages(updatedImages);
        setCheckInFiles(checkInFileMap);
      } catch (error) {
        LoggerService.log('Error downloading image', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchHealthGoalAnswer = async () => {
    try {
      const payload = {
        userId: searchParams?.userId,
        campaignId: searchParams?.campaignId,
      };
      const response = await healthGoalService.userAnswer(payload);

      const responseData = response?.data?.data;

      if (responseData && typeof responseData.options === 'string') {
        try {
          // responseData.options = JSON.parse(responseData.options);
          responseData.options = JSON.parse(responseData.options);
        } catch (error) {
          LoggerService.log('Error parsing options', error);
          responseData.options = null; // or handle it appropriately
        }
      }
      setUserAnswerData(responseData);
      // if (response.status === 200 && typeof response.data.data?.options === 'string') {
      //   setUserAnswerData(response.data.data);
      //   if (response.data.data !== null) {
      //     setHealthGoalData(JSON.parse(response.data.data?.options));
      //     //setIsUpdateMode(true);
      //   }
      // }
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  const fetchHealthGoalQuestion = async () => {
    try {
      const payload = {
        campaignId: '',
      };
      const response = await healthGoalService.campaignQuestion(payload);
      const parsedOptions = JSON.parse(response.data.data.options);

      const campaignQuestionData: CampaignQuestionType = {
        ...response.data.data,
        options: parsedOptions,
      };

      setHealthQuestionData(campaignQuestionData);
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  const fetchParticipantDetails = async () => {
    if (searchParams) {
      setIsLoading(true);
      const result = await apiCall(
        ParticipantService.getParticipantDetail,
        { campaignId: searchParams.campaignId, teamId: searchParams.teamId },
        searchParams.userId ?? '',
      );
      if (result.status === 200) {
        const rawData = result.data.data;

        const modifiedData: ParticipantData = {
          ...rawData,
          weightIn: {
            ...rawData.weightIn,
            otherIndicators: rawData?.weightIn?.otherIndicators
              ? Object.entries(rawData?.weightIn?.otherIndicators || {}).reduce(
                  (acc, [key, value]) => {
                    acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                    return acc;
                  },
                  {} as Record<string, number | null>,
                )
              : null,
          },
          weightOut: {
            ...rawData.weightOut,
            otherIndicators: rawData?.weightOut?.otherIndicators
              ? Object.entries(rawData?.weightOut?.otherIndicators || {}).reduce(
                  (acc, [key, value]) => {
                    acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                    return acc;
                  },
                  {} as Record<string, number | null>,
                )
              : null,
          },
          rewards: rawData.rewards.map((r: DynamicObjectInterface) => {
            return {
              ...r,
              rewardStatus: r.rewardStatus && r.rewardStatus.charAt(0).toUpperCase() + r.rewardStatus.slice(1),
              modifiedDate: r?.modifiedDate ? new Date(convertGMT8date(r?.modifiedDate)) : undefined,
            };
          }),
        };
        setData(modifiedData);
      } else {
        setData(null);
      }
      setIsLoading(false);
    }
  };

  const fetchMasterData = async () => {
    let list = {
      pin: {
        endpoint: PinService.getPinList,
      },
      reward: {
        endpoint: RewardService.getRewardList,
      },
    };
    try {
      const results = await Promise.all(Object.values(list).map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data;
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
    }
  };

  const onBackButtonClick = () => {
    navigate(-1);
  };

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const onSaveParticipantDetails = async (formData: ParticipantData, profileImageUrl: string | undefined) => {
    const activeSection = Object.entries(showEdit)?.find(entry => entry[1] === true)?.[0] as
      | (typeof Sections)[keyof typeof Sections]
      | undefined;

    if (activeSection === 'pointAllocation') {
      let ids: (number | undefined)[] = [];
      const editProductIds = new Set(formData.purchase.map(p => p.recId));
      const editSubsribeIds = new Set(formData.subscribe.map(p => p.recId));
      const productIds = data?.purchase.filter(p => !editProductIds.has(p.recId)).map(p => p.recId);
      const subscribeIds = data?.subscribe.filter(p => !editSubsribeIds.has(p.recId)).map(p => p.recId);
      if (productIds?.length) ids = [...productIds];
      if (subscribeIds?.length) ids = [...ids, ...subscribeIds];
      if (ids?.length) await apiCall(ParticipantService.deletePurchaseProduct, ids as number[], searchParams?.userId!);
    }

    const updatedFormData = {
      ...formData,
      pinLevel: masterData?.pin?.find(p => p.pinName === formData.pinLevel)?.pinId,
      campaignId: searchParams?.campaignId,
      teamId: searchParams?.teamId,
      rewards: formData?.rewards?.map((reward: DynamicObjectInterface) => {
        reward.rewardRecId = reward.rewardRecId?.toString()?.includes('NEW') ? null : reward.rewardRecId;
        return { ...reward };
      }),
      weightIn: {
        ...formData.weightIn,
        weightedDate: formData.weightIn.weightedDate
          ? dateTimeToCustomFormat(formData.weightIn.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData.weightIn.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData.weightIn.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        otherIndicators: Object.entries(formData.weightIn.otherIndicators || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = value ? encryptString(value.toString()) : null;
            return acc;
          },
          {} as Record<string, string | null>,
        ),
      },
      weightOut: {
        ...formData.weightOut,
        weightedDate: formData.weightOut.weightedDate
          ? dateTimeToCustomFormat(formData.weightOut.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData.weightOut.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData.weightOut.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        otherIndicators: Object.entries(formData.weightOut.otherIndicators || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = value ? encryptString(value.toString()) : null;
            return acc;
          },
          {} as Record<string, string | null>,
        ),
      },
      allocation: {
        purchase: {
          products: [...formData.purchase],
          point: parseFloat(formData.point.purchase),
          pointAdj: parseFloat(formData.point.purchaseAdj),
        },
        alp: {
          invoices: [...formData.subscribe],
          point: parseFloat(formData.point.subscribe),
          pointAdj: parseFloat(formData.point.subscribeAdj),
        },
        winningCriteria: {
          point: parseFloat(formData.point.winning),
          pointAdj: parseFloat(formData.point.winningAdj),
        },
        remarks: formData.point.remarks,
      },
    };
    setProfileImageUrl(profileImageUrl);

    const payload = new FormData();
    payload.append('payload', JSON.stringify(updatedFormData));

    resolveFileUploader(formData?.campaign?.configJson, formData?.subChallengeId)?.forEach((i: DynamicObjectInterface) => {
      updatedFormData.weightIn?.file?.[i.fieldCode]?.forEach(({ file }) => {
        if (file) {
          payload.append(`photoId.0,before_result_${i.fieldCode}`, file);
        }
      });
      updatedFormData.weightOut?.file?.[i.fieldCode]?.forEach(({ file }) => {
        if (file) {
          payload.append(`photoId.0,after_result_${i.fieldCode}`, file);
        }
      });
    });

    payload.append('campaignId', searchParams?.campaignId ?? '');
    payload.append('teamId', searchParams?.teamId ?? '');
    payload.append('loginId', searchParams?.userId ?? '');

    const result = await apiCall(
      ParticipantService.updateParticipantDetails,
      payload,
      activeSection ?? '',
      searchParams?.userId!,
    );

    if (result.status === 200) {
      if (!result.data.status) {
        adminToast({
          message: result?.data?.message || t('admin.participant.updateFail'),
          theme: Theme.ERROR,
        });
      } else {
        adminToast({
          message: t('admin.participant.updateSuccess'),
          theme: Theme.SUCCESS,
        });
        await fetchParticipantDetails();
        await fetchMeasurementImage();
        await fetchProfileImage();
      }

      toggleEditModal(activeSection as (typeof Sections)[keyof typeof Sections]);
    } else {
      adminToast({
        message: t('admin.participant.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const onSaveHealthGoal = async (healthGoalData: string | undefined) => {
    try {
      const payload = {
        userId: searchParams?.userId,
        faqId: 1,
        answerText: '',
        options: healthGoalData,
        campaignId: searchParams?.campaignId,
        recId: userAnswerData?.answerId || null,
      };
      const response = await healthGoalService.updateHealthGoal(payload);
      if (response.status === 200) {
        adminToast({
          message: t('admin.participant.updateSuccess'),
          theme: Theme.SUCCESS,
        });
        //
        await fetchHealthGoalAnswer();
        toggleEditModal(Sections.HealthGoal);
      } else {
        adminToast({
          message: t('admin.participant.updateFail'),
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error uploading data', error);
    }
  };

  const getButtonConfig = useMemo(() => {
    let commonConfig = {
      iconName: 'pencil' as IconName,
      iconStyle: IconStyle.SOLID,
      theme: Theme.WHITE,
      title: t('admin.participant.edit'),
    };
    return {
      participantDetails: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.ParticipantDetails) }],
      healthGoal: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.HealthGoal) }],
      pointAllocation: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.PointAllocation) }],
      rewardGift: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.RewardGift) }],
      result: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.Result) }],
    } as { [index: string]: IconButtonProps[] };
  }, []);

  const showModal = (
    <>
      {showEdit.participantDetails && (
        <DetailsModal
          data={data}
          masterData={masterData}
          searchParams={searchParams}
          initialProfileImageUrl={profileImageUrl}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.ParticipantDetails)}
        />
      )}
      {showEdit.healthGoal && (
        <HealthGoalModal
          data={data}
          onSave={onSaveHealthGoal}
          healthQuestionData={healthQuestionData}
          userAnswerData={userAnswerData}
          onCancel={() => toggleEditModal(Sections.HealthGoal)}
        />
      )}
      {showEdit.pointAllocation && (
        <PointAllocationModal
          data={data}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.PointAllocation)}
        />
      )}
      {showEdit.rewardGift && (
        <RewardGiftModal
          data={data}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.RewardGift)}
          masterData={masterData}
        />
      )}
      {showEdit.weightDetails && (
        <ResultModal
          data={data}
          searchParams={searchParams}
          onSave={onSaveParticipantDetails}
          onCancel={() => toggleEditModal(Sections.Result)}
          masterData={masterData}
          measurementImages={images}
        />
      )}
    </>
  );

  // const handleDownloadResult = async (assessmentType: string, partyId: string) => {
  //   try {
  //     const payload = {
  //       loginId: searchParams?.userId,
  //       campaignId: searchParams?.campaignId,
  //       teamId: searchParams?.teamId,
  //       assessmentType: assessmentType,
  //     };

  //     const response = await apiCall(AttachmentService.downloadOther, payload);

  //     //console.log('response', response);
  //     if (response.status === 200 && !response.data?.data?.message) {
  //       const data = response.data;
  //       const blob = new Blob([data], { type: 'application/zip' });

  //       const link = document.createElement('a');
  //       link.href = window.URL.createObjectURL(blob);
  //       link.setAttribute('download', partyId + '_' + assessmentType + '_result.zip');
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);

  //       // Release the object URL resources
  //       URL.revokeObjectURL(link.href);
  //     } else {
  //       adminToast({
  //         message: response?.data?.data?.message,
  //         theme: Theme.ERROR,
  //       });
  //     }
  //   } catch (error) {
  //     LoggerService.log('Error exporting CSV', error);
  //   }
  // };

  return (
    <div className={styles['parti']}>
      <AdminPageHeader withBackButton onBackButtonClick={onBackButtonClick} title={t('admin.participant.title')} />
      <div className={styles['parti-spacing-v']} style={{ marginTop: '0.5rem' }}>
        <Card
          title={t('admin.participant.detailsTitle')}
          headerStyle={HeaderStyle.HEADER_5}
          buttonType={ButtonType.ICON}
          button={getButtonConfig.participantDetails}
        >
          <div className={styles['details-top']}>
            <div className={styles['details-top-profile-img']}>
              <ProfileImage
                placeholder={data?.name ?? ''}
                src={profileImageUrl ? profileImageUrl : Personalize}
                isLoading={isLoading}
              />
            </div>
            <div className={styles['details-top-info']}>
              <h3 className={styles['right-offset']}>
                {t('admin.participant.campaign')}: {data?.campaign?.campaignName ?? '-'}
              </h3>
              <div className={styles['details-top-background']}>
                <div className={styles['layout'] + ' ' + styles['right-offset']}>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.aboIdEnrollName')}</div>
                    <p>
                      {!!data?.loginId && !!data?.name
                        ? `${data.loginId} / ${data.name} ${data.memberType ? `(${data.memberType})` : ''}`
                        : '-'}
                    </p>
                  </div>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.pinLevel')}</div>
                    <p>{data?.pinLevel ?? '-'}</p>
                  </div>
                  <div className={styles['details-top-info-items']}>
                    <div className={styles['label']}>{t('admin.participant.status')}</div>
                    <p>{data?.enrollmentStatus ?? '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles['details-bottom']}>
            <div className={styles['details-bottom-info']}>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.email')}</div>
                <p>{data?.email ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamName')}</div>
                <p>{data?.team?.teamName ?? 'N/A'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamCoach')}</div>
                <p>{!!data?.coachID && !!data?.coachName ? `${data.coachID} - ${data.coachName}` : 'N/A'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>Participation Type</div>
                <p className={styles['radio-button-container']}>
                  <RadioButton
                    label={t('dashboard.create-team.input.team')}
                    checked={data?.team?.participationType === 'team'}
                    disabled={true}
                  />
                  <RadioButton
                    label={t('dashboard.create-team.input.individual')}
                    checked={data?.team?.participationType === 'ind'}
                    disabled={true}
                  />
                </p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.gender')}</div>
                <p>{data?.gender ?? '-'}</p>
              </div>
            </div>
            <div className={styles['details-bottom-info']}>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.phoneNumber')}</div>
                <p>{data?.phone ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamPeriod')}</div>
                <p>
                  {!!data?.team?.teamStartDate && !!data?.team?.teamEndDate
                    ? `${formatDateDMYYYY(new Date(data?.team?.teamStartDate))} - ${formatDateDMYYYY(
                        new Date(data?.team?.teamEndDate),
                      )}`
                    : '-'}
                </p>
              </div>
              {/* <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.teamLeader')}</div>
                <p>
                  {!!data?.team?.teamLeaderLoginId && !!data?.team?.teamLeaderName
                    ? `${data?.team?.teamLeaderLoginId} - ${data?.team?.teamLeaderName}`
                    : '-'}
                </p>
              </div> */}
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.ageGroup')}</div>
                <p>{data?.ageGroup ?? '-'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.isNewAbo')}</div>
                <p>{data?.isNewAbo === undefined ? '-' : data?.isNewAbo ? 'YES' : 'NO'}</p>
              </div>
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>{t('admin.participant.remarks')}</div>
                <p>{data?.remark ?? '-'}</p>
              </div>
            </div>
          </div>
        </Card>
        {data?.campaign?.configJson?.campaignIsHealthGoalSectionShown === 1 && (
          <Card
            title={t('admin.participant.healthGoal')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.healthGoal}
          >
            <div className={styles['details-top-info-items']}>
              <div>
                {userAnswerData !== null && userAnswerData?.options && (
                  <div className={styles['health-goal-section']}>
                    {Array.isArray(userAnswerData?.options) &&
                      userAnswerData.options.map(option => (
                        <div key={option.key} className={styles['health-goal-item']}>
                          {option.title}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
        <div className={styles['layout']}>
          <Card
            title={t('admin.participant.pointAllocationTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.pointAllocation}
          >
            <div className={styles['allocation']}>
              {/* <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderNumber')}</div>
                <p>{data && data.purchase.length > 0 ? data.purchase.map((p) => p.orderNumber).join(', ') : '-'}</p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.product')}</div>
                <p>BodyKey Start Pack</p>
              </div> */}
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderNumber')}</div>
                <div>
                  {data && data?.purchase?.length ? data?.purchase.map((p, i) => <p key={i}>{p.orderNumber}</p>) : '-'}
                </div>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.productName')}</div>
                <div>
                  {data && data?.purchase?.length ? data.purchase.map((p, i) => <p key={i}>{p.orderName}</p>) : '-'}
                </div>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderStatus')}</div>
                {data && data?.purchase?.length
                  ? data.purchase.map((p, i) => (
                      <p key={i}>{p.orderStatus === null ? '-' : p.orderStatus === 1 ? 'YES' : 'NO'}</p>
                    ))
                  : '-'}
              </div>
              <hr className={styles['horizontal-divider']} />
              {/* <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.alpInvoiceNumber')}</div>
                <p>{data && data?.subscribe?.length > 0 ? data?.subscribe.map(p => p.orderNumber).join(', ') : '-'}</p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.orderStatus')}</div>
                {data && data?.subscribe?.length
                  ? data.subscribe.map((p, i) => (
                      <p key={i}>{p.orderStatus === null ? '-' : p.orderStatus === 1 ? 'YES' : 'NO'}</p>
                    ))
                  : '-'}
              </div>
              <hr className={styles['horizontal-divider']} /> */}
              {/*<div className={styles['allocation-item']}>
                <div className={styles['label']}>Remarks</div>
                <p>{data?.point.remarks}</p>
              </div> */}
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.completed')}</div>
                <p>
                  {data && data?.isIndividualCompleted === null
                    ? '-'
                    : data?.isIndividualCompleted === 1
                      ? 'YES'
                      : 'NO'}
                </p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.achieved')}</div>
                <p>
                  {data && data?.isIndividualAchieved === 1
                    ? 'YES'
                    : data?.isIndividualAchieved === 0
                      ? 'NO'
                      : 'PENDING'}
                </p>
              </div>
              <div className={styles['allocation-item']}>
                <div className={styles['label']}>{t('admin.participant.winning')}</div>
                <p>{data && data?.isGroupAchieved === 1 ? 'YES' : data?.isGroupAchieved === 0 ? 'NO' : 'PENDING'}</p>
              </div>
              {/* Task Completed = all configured check-in days submitted (only shown
                  when the sub-challenge configures check-in). */}
              {checkInDays.length > 0 && (
                <div className={styles['allocation-item']}>
                  <div className={styles['label']}>{t('admin.participant.taskCompleted')}</div>
                  <p>{checkInDays.every(d => submittedCheckInByDay.has(d)) ? 'YES' : 'PENDING'}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Check-In submissions (read-only) — when + what the participant uploaded.
              Only shown when the sub-challenge configures check-in days. Clicking a
              file name triggers a download (same as the My Progress page). Kept in the
              same row as Progress Details so both share the row's width (Reward moves
              to its own row below to avoid text truncation). */}
          {checkInDays.length > 0 && (
            <Card title={t('admin.participant.checkInTitle') || 'Check-In'} headerStyle={HeaderStyle.HEADER_5}>
              <TableContainer
                dataSource={checkInTableData}
                getColumns={getCheckInColumns}
                cb={{ onDownload: handleDownloadCheckInFile }}
                pagination={false}
                rowKey="key"
                hideSearchFilter
              />
            </Card>
          )}
        </div>
        <div className={styles['layout']}>
          <Card
            title={t('admin.participant.rewardTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.rewardGift}
          >
            <TableContainer
              dataSource={data?.rewards ?? []}
              getColumns={getRewardListConfig}
              pagination={false}
              rowKey="rewardRecId"
            />
          </Card>
        </div>
        <div className={styles['layout']}>
          <Card
            title={t('admin.participant.resultTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.result}
          >
            {/* Participant's joined sub-challenge (MULTI campaigns only), shown right
                under the Assessment Details header so the admin knows which
                sub-challenge the values below belong to. */}
            {resolveSubChallenge(data?.campaign?.configJson, data?.subChallengeId)?.name && (
              <div className={styles['label-container']}>
                <div className={styles['label']}>{t('admin.participant.subChallenge')}</div>
                <p>{resolveSubChallenge(data?.campaign?.configJson, data?.subChallengeId)?.name}</p>
              </div>
            )}
            <div className={styles['result-container']}>
              <div className={styles['measurement-wrap']}>
                {resolveProfileImageSectionShown(data?.campaign?.configJson, data?.subChallengeId) && (
                  <div className={styles['carousel-container']}>
                    <ImageSlider
                      id="beforeImg"
                      slides={[
                        {
                          src: images?.before_front?.url ? images?.before_front?.url : PhotoUnavailable,
                          alt: 'Before Front View',
                        },
                        {
                          src: images?.before_side?.url ? images?.before_side?.url : PhotoUnavailable,
                          alt: 'Before Side View',
                        },
                      ]}
                      onClick={() => toggleEditModal(Sections.Result)}
                    />
                    <div className={styles['label-container']}>
                      <div className={styles['label']}>{t('admin.participant.remark')}</div>
                      <p>{data?.weightIn?.remarks ?? '-'}</p>
                    </div>
                  </div>
                )}
                <div className={styles['result-info']}>
                  <div className={styles['title']}>{t('admin.participant.before')}</div>
                  <div className={styles['label-container']}>
                    <div className={styles['label']}>{t('admin.participant.weighInDate')}</div>
                  </div>
                  <div className={styles['tag-calendar']}>
                    <div className={styles['tag-calendar']}>
                      <Tag
                        theme={Theme.SUCCESS}
                        text={
                          data?.weightIn?.weightedDate !== null
                            ? dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'DD/MM/yyyy')
                            : '-'
                        }
                        style={TagStyle.OUTLINE}
                      />
                    </div>
                  </div>
                  {/* Test device the participant used (resolved from the sub-challenge's
                      configured devices), shown below the date. Only when configured. */}
                  {resolveTestDeviceName(data?.campaign?.configJson, data?.subChallengeId, data?.weightIn?.machine) && (
                    <div className={styles['label-container']}>
                      <div className={styles['label']}>{t('admin.participant.testDevice') || 'Test Device'}</div>
                      <p>{resolveTestDeviceName(data?.campaign?.configJson, data?.subChallengeId, data?.weightIn?.machine)}</p>
                    </div>
                  )}
                  {preSubKpiCode?.map(
                    (i: DynamicObjectInterface) => (
                      <div className={styles['label-container']}>
                        <div className={styles['label']}>
                           <span className={styles['item-tooltip']}>
                            {i.indicatorName} ({i.indicatorMeasurementUnit})
                            {i.disablePreAssessment === 1 && (
                              <Tooltip
                              message={t('dashboard.my-progress.noPreAssessmentTooltip')}
                              preference={TooltipPreference.RIGHT}
                              className={styles['input-tool-tip']}
                              iconName="circle-exclamation"
                            />
                            )}
                           </span>
                        </div>
                        <p>{data?.weightIn?.otherIndicators?.[i.subKpiCode] ?? '-'}</p>
                      </div>
                    ),
                  )}
                  {/* {images?.before_result?.url && (
                    <div className={styles['download-container']}>
                      <Button
                        title={'Download Attachments'}
                        type={ButtonTypes.BUTTON}
                        size={ButtonSize.SMALL}
                        onClick={() => handleDownloadResult('pre-assessment', data?.partyId || '')}
                      />
                    </div>
                  )} */}
                  {/* <div className={styles['label-container']}>
                    <div className={styles['label']}>{t('admin.participant.acknowledgedBy')}</div>
                    <p>{data?.weightIn?.acknowledgeByName ?? '-'}</p>
                  </div>
                  <div className={styles['label-container-ack']}>
                    <div className={styles['label']}>{t('admin.participant.ackowLedged')}</div>
                    <div className={styles['label-date']}>{t('admin.participant.acknowLedgedOfDate')}</div>
                    <p>
                      {data?.weightIn?.acknowledgeDateTime !== null && data?.weightIn?.acknowledgeDateTime !== undefined
                        ? dateTimeToCustomFormat(data?.weightIn?.acknowledgeDateTime ?? '', 'DD/MM/yyyy')
                        : '-'}
                    </p>
                  </div> */}
                </div>
              </div>

              <div className={styles['measurement-wrap']}>
                {resolveProfileImageSectionShown(data?.campaign?.configJson, data?.subChallengeId) && (
                  <div className={styles['carousel-container']}>
                    <ImageSlider
                      id="afterImg"
                      slides={[
                        {
                          src: images?.after_front?.url ? images?.after_front?.url : PhotoUnavailable,
                          alt: 'After Front View',
                        },
                        {
                          src: images?.after_side?.url ? images?.after_side?.url : PhotoUnavailable,
                          alt: 'Afer Side View',
                        },
                      ]}
                      onClick={() => toggleEditModal(Sections.Result)}
                    />
                    <div className={styles['label-container']}>
                      <div className={styles['label']}>{t('admin.participant.remark')}</div>
                      <p>{data?.weightOut?.remarks ?? '-'}</p>
                    </div>
                  </div>
                )}
                <div className={styles['result-info']}>
                  <div className={styles['title']}>{t('admin.participant.after')}</div>
                  <div className={styles['label-container']}>
                    <div className={styles['label']}>{t('admin.participant.weighOutDate')}</div>
                  </div>
                  <div className={styles['tag-calendar']}>
                    <Tag
                      theme={Theme.SUCCESS}
                      text={
                        data?.weightOut?.weightedDate !== null
                          ? dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'DD/MM/yyyy')
                          : '-'
                      }
                      style={TagStyle.OUTLINE}
                    />
                  </div>
                  {/* Test device (post-assessment), shown below the date when configured. */}
                  {resolveTestDeviceName(data?.campaign?.configJson, data?.subChallengeId, data?.weightOut?.machine) && (
                    <div className={styles['label-container']}>
                      <div className={styles['label']}>{t('admin.participant.testDevice') || 'Test Device'}</div>
                      <p>{resolveTestDeviceName(data?.campaign?.configJson, data?.subChallengeId, data?.weightOut?.machine)}</p>
                    </div>
                  )}
                  {postSubKpiCode?.map(
                    (i: DynamicObjectInterface) => {

                      const weightInValue = data?.weightIn?.otherIndicators?.[i.subKpiCode];
                      const weightOutValue = data?.weightOut?.otherIndicators?.[i.subKpiCode];
                      const hasPost = weightOutValue !== null && weightOutValue !== undefined && !!data?.weightOut?.weightedDate;

                      // Gender/age-threshold (e.g. Grip Strength) and disable-pre (e.g.
                      // Sit to Stand) indicators have no meaningful pre/post numeric diff
                      // — the old subtraction produced NaN / a misleading number. Judge
                      // win/lose against the resolved threshold / target instead and show
                      // a green tick (win) or red cross (lose). Mirrors the BFF
                      // checkIndividualAchieved logic so display matches scoring.
                      const genderAgeThreshold = resolveGenderAgeThreshold(i, data?.gender, data?.ageGroup);
                      const isThresholdIndicator = !!i?.indicatorWinningCriteria && genderAgeThreshold !== null;
                      const isDisablePreIndicator =
                        !isThresholdIndicator && (i?.disablePreAssessment === 1 || i?.ignorePreAssessment === 1);
                      const isWinLose = isThresholdIndicator || isDisablePreIndicator;

                      let winLoseAchieved = false;
                      if (isWinLose && hasPost) {
                        const measured = getRoundedNumber(weightOutValue);
                        const standard = isThresholdIndicator
                          ? (genderAgeThreshold as number)
                          : parseFloat(i?.targetFrom);
                        winLoseAchieved =
                          !isNaN(standard) &&
                          (i?.indicatorWinningCriteria === 'loss' ? measured <= standard : measured >= standard);
                      }

                      // Standard indicators: signed pre/post difference (or percentage).
                      const weightOutDiffValues =
                       weightInValue !== null &&
                        weightOutValue !== null
                        ? i?.percentageCalculation === 1
                              ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
                              : getRoundedNumber(weightOutValue - weightInValue)
                            : 0;

                      // Colour the standard diff by ACHIEVEMENT (not raw sign), matching
                      // the participant My Progress view: for a 'loss' indicator a
                      // decrease is an improvement, so direction-normalise before
                      // comparing to targetFrom. Mirrors the BFF checkIndividualAchieved.
                      const normalizedDiff =
                        i?.percentageCalculation === 1
                          ? i?.indicatorWinningCriteria === 'loss'
                            ? getRoundedNumber(((weightInValue - weightOutValue) / weightInValue) * 100)
                            : getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
                          : i?.indicatorWinningCriteria === 'loss'
                            ? weightInValue - weightOutValue
                            : weightOutValue - weightInValue;
                      const diffTarget = parseFloat(i?.targetFrom);
                      const diffAchieved = !isNaN(diffTarget) && normalizedDiff >= diffTarget;

                      return (
                        <div key={i.subKpiCode} className={styles['label-container']}>
                          <div className={styles['label']}>
                            {i.indicatorName} ({i.indicatorMeasurementUnit})
                          </div>
                          <p>{weightOutValue ?? '-'}</p>
                          {isWinLose
                            ? hasPost && (
                                <FAIcon
                                  iconStyle={IconStyle.SOLID}
                                  iconName={winLoseAchieved ? 'check' : 'xmark'}
                                  theme={winLoseAchieved ? Theme.SUCCESS : Theme.ERROR}
                                  className={styles['result-winlose-icon']}
                                />
                              )
                            : weightInValue !== null && weightOutValue !== null && data?.weightOut?.weightedDate && (
                                <div
                                  className={
                                    diffAchieved ? styles['change-achieved'] : styles['change-missed']
                                  }
                                >
                                  ({weightOutDiffValues > 0 ? '+' : ''}
                                  {weightOutDiffValues.toFixed(1)}
                                  {i?.percentageCalculation === 1 ? '%' : ''})
                                </div>
                              )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div>
          {progressData && data?.campaign && !!data?.campaign?.configJson?.campaignIsProgressMonitoringSectionShown && (
            <Card 
              title={t('dashboard.my-team.participant-profile.label.progressMonitoring')}
              headerStyle={HeaderStyle.HEADER_5}>
               <>
              {/* interim measurement  */}
              <div className={styles['progress-monitoring']}>
                {/* <div>Monitor your health stats and see your improvement over time!</div> */}
                <TableWithAction
                  actionColumnWidth={5}
                  enableCheckbox={false}
                  dataSource={progressData ?? []}
                  columns={progressMonitorConfig}
                  pagination={true}
                  rowKey={'recId'}
                  menuItems={[]}
                  iconProps= {[
                    {
                      iconName: 'download',
                      iconStyle: IconStyle.SOLID,
                      isDisabled: (row) => row?.fileAvailable === 0,
                      onIconClick: (row) => downloadIntermediateResult(row)
                    }]}
                  actionType={ActionType.SINGLE_ACTION_ICON}
                />
              </div>
            </>
            </Card>
            )}
        </div>
        <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={auditHistoryLog?.length <= 0} />
      </div>
      {showModal}
    </div>
  );
};
